import type { ThemeAssetReference } from '../theme-contract/types';
import type {
  AssignedChannelDto,
  DeleteThemeErrorDto,
  ChannelThemeAssetDto,
  ChannelThemeDto,
  ChannelThemesResponseDto,
  ChannelThemeSummaryDto,
  ChannelThemeWriteRequestDto,
} from './channel-themes.api.types';
import type {
  AccountTheme,
  AccountThemeList,
  AssignedThemeChannel,
  ThemeDeleteError,
  AccountThemeSummary,
  CreateChannelThemeArgs,
  UpdateChannelThemeArgs,
} from './types';

export function toApiChannelThemesUrl(accountId: string) {
  return `/v2/accounts/${accountId}/channel_themes`;
}

// Without an action, the theme's own URL.
export function toApiChannelThemeUrl(accountId: string, themeId: string, action?: 'clone' | 'delete' | 'update') {
  const themeUrl = `${toApiChannelThemesUrl(accountId)}/${themeId}`;

  return action ? `${themeUrl}/${action}` : themeUrl;
}

export function toApiChannelThemeAssetUploadUrl(accountId: string) {
  return `${toApiChannelThemesUrl(accountId)}/direct_upload`;
}

export function toApiChannelThemeAssetUpload(file: File): FormData {
  const formData = new FormData();

  formData.append('file', file);

  return formData;
}

// The upload result goes into the theme document's image field as is; its snake_case is the API's image contract.
export function fromApiChannelThemeAsset({
  asset,
  blob_id,
  file_name,
  url,
}: ChannelThemeAssetDto): ThemeAssetReference {
  return { asset, blob_id, file_name, url };
}

export function fromApiChannelThemeSummary({ id, name }: ChannelThemeSummaryDto): AccountThemeSummary {
  return { id, name };
}

function activeChannels(channels: AssignedChannelDto[]): AssignedThemeChannel[] {
  return channels.filter((channel) => !channel.deleted_at).map(({ id, name, type }) => ({ id, name, type }));
}

export function fromApiThemeDeleteError(status: number, response?: DeleteThemeErrorDto): ThemeDeleteError {
  const channels = response?.data?.channels;

  return {
    blockingChannels:
      status === 400 && response?.code === 13008 && Array.isArray(channels) ? activeChannels(channels) : null,
    status,
  };
}

export function fromApiChannelThemes({ data, limit_per_account }: ChannelThemesResponseDto): AccountThemeList {
  return {
    limitPerAccount: limit_per_account,
    themes: data.map((theme) => ({
      ...fromApiChannelThemeSummary(theme),
      channels: theme.channels ? activeChannels(theme.channels) : null,
    })),
  };
}

export function fromApiChannelTheme({ id, meta, name }: ChannelThemeDto): AccountTheme {
  return { id, name, theme: meta };
}

export function toApiChannelThemeWrite({
  name,
  theme,
}: Pick<CreateChannelThemeArgs | UpdateChannelThemeArgs, 'name' | 'theme'>): ChannelThemeWriteRequestDto {
  return {
    ...(name === undefined ? {} : { name }),
    ...(theme === undefined ? {} : { meta: theme }),
  };
}
